import { LogIn, Pickaxe, UserPlus } from "lucide-react";
import { FormEvent, useState } from "react";
import { messageOf } from "../ui/format";
import { type EntryAuthMode } from "../ui/navigation";

export function AuthEntryScreen({
  onLogin,
  onRegister,
}: {
  onLogin: (username: string, password: string) => Promise<void>;
  onRegister: (
    username: string,
    password: string,
    passwordConfirm: string,
    nickname: string,
  ) => Promise<void>;
}) {
  const [mode, setMode] = useState<EntryAuthMode>("login");
  const [username, setUsername] = useState("");
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const run = async (operation: () => Promise<void>) => {
    setPending(true);
    setError("");
    try {
      await operation();
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setPending(false);
    }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mode === "register" && password !== passwordConfirm) {
      setError("两次输入的密码不一致");
      return;
    }
    await run(() =>
      mode === "login"
        ? onLogin(username, password)
        : onRegister(username, password, passwordConfirm, nickname),
    );
  };
  const changeMode = (next: EntryAuthMode) => {
    setMode(next);
    setError("");
    setPassword("");
    setPasswordConfirm("");
  };
  return (
    <main className="auth-screen">
      <section className="auth-dialog auth-entry" aria-labelledby="auth-entry-title">
        <div className="auth-brand" aria-hidden="true">
          <Pickaxe size={25} />
        </div>
        <p className="eyebrow">ACCOUNT</p>
        <h1 id="auth-entry-title">进入 Breacher</h1>
        <div className="auth-mode-tabs" role="tablist" aria-label="账号入口">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            onClick={() => changeMode("login")}
          >
            登录
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            onClick={() => changeMode("register")}
          >
            注册
          </button>
        </div>
        <form onSubmit={submit}>
          <label>
            用户名
            <input
              type="text"
              inputMode="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              minLength={3}
              maxLength={24}
              pattern="[A-Za-z0-9_]{3,24}"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>
          {mode === "register" && (
            <label>
              昵称
              <input
                type="text"
                autoComplete="nickname"
                minLength={2}
                maxLength={16}
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                required
              />
            </label>
          )}
          <label>
            密码
            <input
              type="password"
              minLength={10}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {mode === "register" && (
            <label>
              确认密码
              <input
                type="password"
                minLength={10}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(event) => setPasswordConfirm(event.target.value)}
                required
              />
            </label>
          )}
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          <button className="primary-command compact" disabled={pending}>
            {mode === "login" ? <LogIn size={18} /> : <UserPlus size={18} />}
            {pending ? "处理中" : mode === "login" ? "登录" : "注册账号"}
          </button>
        </form>
      </section>
    </main>
  );
}
